import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './StripePaymentForm.css';
import { Form, Row, Col } from 'react-bootstrap';
import { LargeButton } from '../../components';

// Country codes data
const countries = [
  { code: 'CA', name: 'Canada' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'NO', name: 'Norway' },
  { code: 'NZ', name: 'New Zealand' }
];

// Canadian provinces and territories
const canadianProvinces = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

const StripePaymentForm = ({ onSubmit, onCancel, isProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    address: '',
    country: '',
    province: '',
    postalCode: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!stripe || !elements) {
      setError("Stripe is not initialized yet. Please try again.");
      return;
    }

    if (!billingDetails.country) {
      setError("Please select a country");
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Create a payment method using the card element
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          address: {
            line1: billingDetails.address,
            country: billingDetails.country,
            state: billingDetails.province,
            postal_code: billingDetails.postalCode
          }
        }
      });

      if (error) {
        console.error("Stripe error:", error);
        setError(error.message);
      } else {
        // Send the payment method ID to your server
        onSubmit(paymentMethod.id, billingDetails);
      }
    } catch (err) {
      console.error("Payment processing error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails({
      ...billingDetails,
      [name]: value
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Form.Group as={Col} md="12">
          <Form.Label>Card Holder Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Name on card"
            name="name"
            value={billingDetails.name}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} md="12">
          <Form.Label>Card Information</Form.Label>
          <div className="stripe-card-element">
            <CardElement 
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
                wallet: {
                  allowedPaymentMethods: ['card', 'link'],
                  applePay: 'auto',
                  googlePay: 'auto',
                }
              }}
            />
          </div>
          {error && <div className="text-danger mt-2">{error}</div>}
        </Form.Group>
      </Row>

      <hr />

      <Row>
        <Form.Group as={Col} md="12">
          <Form.Label>Billing Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="Please put address"
            name="address"
            value={billingDetails.address}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} md="12">
          <Form.Label>Country</Form.Label>
          <Form.Control
            as="select"
            name="country"
            value={billingDetails.country}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Country</option>
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} md="6">
          <Form.Label>Province</Form.Label>
          {billingDetails.country === 'CA' ? (
            <Form.Control
              as="select"
              name="province"
              value={billingDetails.province}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Province</option>
              {canadianProvinces.map(province => (
                <option key={province.code} value={province.code}>
                  {province.name}
                </option>
              ))}
            </Form.Control>
          ) : (
            <Form.Control
              type="text"
              placeholder="Province/State"
              name="province"
              value={billingDetails.province}
              onChange={handleInputChange}
              required
            />
          )}
        </Form.Group>

        <Form.Group as={Col} md="6">
          <Form.Label>Postal Code</Form.Label>
          <Form.Control
            type="text"
            placeholder="Postal Code"
            name="postalCode"
            value={billingDetails.postalCode}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Row>

      <div className="sticky-bottom" style={{ flexDirection: 'column' }}>
        <Col md='12'>
          <span data-description="Landlord submitted payment for package" id="BTN-listing-step-6-payment-submit">
          <LargeButton
            title={isProcessing ? "Processing..." : "Submit Payment"}
            type="submit"
            disabled={!stripe || isProcessing}
          />
          </span>

          <span data-description="Landlord cancelled payment for package" id="BTN-listing-step-6-payment-cancel">
          <LargeButton
            variant="secondary"
            title="Cancel"
            onClick={onCancel}
            disabled={isProcessing}
          />
          </span>
        </Col>
      </div>
    </Form>
  );
};

export default StripePaymentForm;